import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface WorkStyleDimension {
  expectation: number;
  description: string;
  candidate_result: number;
  score_range: string;
  implication: string;
  impact_on_work: string;
  opportunities_for_growth: string[];
  good_at: string[];
  poor_at: string[];
}

export interface WorkStyleData {
  score: number;
  prioritizing: WorkStyleDimension;
  visualizing: WorkStyleDimension;
  planning: WorkStyleDimension;
  coordinating: WorkStyleDimension;
  version: number;
}

interface WorkStyleResponse {
  message: string;
  data: WorkStyleData;
}

/**
 * Hook for fetching work style data
 */
export const useWorkStyle = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['workStyle', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<WorkStyleResponse>(
        `/reports/${reportId}/work-style/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch work style';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    workStyle: data,
    isLoading,
    error,
    refetch,
  };
};
